import { GST_RANGE_KEYS, GstRangeKey, GstRangeParams } from '../store/dashboard/revenueVendorExpenses/types';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const INDIA_TZ = 'Asia/Kolkata';

export const getTimeStamp = () => dayjs().format(`YYYYMMDDHH:mm:ss`);
export const getTimeStampForFileName = () => {
    const randomMinutes = Math.floor(Math.random() * 20) + 1; // adding random minutes to avoid duplicate file names
    const actualMinutes = dayjs().diff(dayjs().startOf('day'), 'minutes');
    const totalMinutes = actualMinutes + randomMinutes;
    return dayjs().format(`D MMM YYYY`) + `(${totalMinutes})`; // returns 17 Jan 2025 (1309)
};

/**
 * Adds a human-readable timestamp to filename (e.g., "file - 16 Dec 2025(834).pdf")
 */
export const addTimeStampForFileName = (fileName: string) => {
    const fileExtension = fileName.split('.').pop();
    const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
    return `${fileNameWithoutExtension} - ${getTimeStampForFileName()}.${fileExtension}`;
};

export const getMonthsByYear = (numOfMonths: number) => {
    const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

    let endDate = dayjs();
    let endDateMonth = endDate.month();
    let endDateYear = endDate.year();
    let startDate = dayjs().subtract(numOfMonths, 'months');
    let startDateYear = startDate.year();
    let datesObj: any = {};
    for (let i = startDateYear; i <= endDateYear; i++) {
        let monthsArr;
        if (i === endDateYear) monthsArr = months.filter((month) => parseInt(month) <= endDateMonth);
        else monthsArr = months;
        datesObj[i] = monthsArr;
    }
    return { months, datesObj, startDate, endDate };
};

export const monthsEnum: { [x: string]: { name: string; fullName: string } } = {
    '01': {
        name: 'Jan',
        fullName: 'January',
    },
    '02': {
        name: 'Feb',
        fullName: 'February',
    },
    '03': {
        name: 'Mar',
        fullName: 'March',
    },
    '04': {
        name: 'Apr',
        fullName: 'April',
    },
    '05': {
        name: 'May',
        fullName: 'May',
    },
    '06': {
        name: 'Jun',
        fullName: 'June',
    },
    '07': {
        name: 'Jul',
        fullName: 'July',
    },
    '08': {
        name: 'Aug',
        fullName: 'August',
    },
    '09': {
        name: 'Sep',
        fullName: 'September',
    },
    '10': {
        name: 'Oct',
        fullName: 'October',
    },
    '11': {
        name: 'Nov',
        fullName: 'November',
    },
    '12': {
        name: 'Dec',
        fullName: 'December',
    },
};

export const DATE_FORMATS = {
    ISO: 'YYYY-MM-DD',
    MONTH_FULL_YEAR: 'MMMM YYYY',
    MONTH_SHORT_YEAR: 'MMM YYYY',
    DATE_FORMAT_DDMMYYYY: 'DD-MM-YYYY',
    DATE_FORMAT_DD_MM_YYYY_SLASH: 'DD/MM/YYYY',
    DATE_FORMAT_MM_DD_YYYY_SLASH: 'MM/DD/YYYY',
    DATE_FORMAT_D_MMM_YYYY: 'D MMM YYYY',
    DATE_FORMAT_DD_MMM_YYYY: 'DD MMM YYYY',
    TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY: 'hh:mm A, DD MMM YYYY',
    DATE_TIME_DD_MMM_YYYY_HH_MM_A: 'DD MMM YYYY hh:mm A',
    TIME_DATE_FORMAT_H_MM_A_DD_MMMM_YYYY: 'h:mm A, DD MMMM YYYY',
    TIME_DATE_FORMAT_HH_MM_a_DD_MMM_YYYY: 'hh:mm a, DD MMM YYYY',
    FILENAME_SUFFIX_DD_MM_HH_MM_SS: 'DD_MM_HH_mm_ss',
    // Matches aica's Consent Audit Trail Content-Disposition filename suffix.
    FILENAME_SUFFIX_YYYYMMDDHHMM: 'YYYYMMDDHHmm',
    HISTORY_DRAWER: 'h:mm a, D MMM YYYY',
    // Same layout as HISTORY_DRAWER but explicitly labelled IST — pair with formatDateInIST.
    HISTORY_DRAWER_IST: 'h:mm a, D MMM YYYY [IST]',
    CARD_COMMENT_TIMESTAMP: 'h:mm a, DD MMM YYYY',
    LIBRARY_FILE_TIMESTAMP: 'h:mm a, D MMMM YYYY',
} as const;

export function getFormattedDate(date: string, periodSelectorVariant: string | null, isFinancials = false) {
    //USED IN DATEPICKER
    if (!date || !periodSelectorVariant) return '-';

    const dateTime = dayjs(date);
    if (isFinancials) return dateTime.format('YYYY');

    switch (periodSelectorVariant) {
        case 'Monthly':
            return dateTime.format("MMM'YY");
        case 'Quarterly': {
            let fyQuarter = Math.ceil((parseInt(dateTime.format('MM')) - 3) / 3);
            fyQuarter = fyQuarter <= 0 ? 4 : fyQuarter;
            return `Q${fyQuarter}'FY${parseInt(dateTime.add(parseInt(dateTime.format('MM')) > 3 ? 1 : 0, 'years').format('YY'))}`;
        }
        case 'Yearly':
            return dateTime.format('YYYY');
        default:
            return dateTime.format("MMM'YY");
    }
}

export function initializeDatepickerDates(periodSelectorVariant: string | null) {
    const DATE_FORMAT = 'YYYY-MM-DD';
    if (!periodSelectorVariant) return [];
    //Initializing dates for non recurring metrics. (Graphs in Section 1 of Dashboard)
    const now = dayjs();
    // const now = dayjs('2023-04-01');
    const previousMonthLastDate = now.subtract(1, 'month').endOf('month');
    const monthlyStartDate = previousMonthLastDate.subtract(11, 'months').startOf('month');

    const currentQuarterStart = now.startOf('quarter');
    const previousQuarterEnd = currentQuarterStart.subtract(1, 'day');
    const quarterlyStartDate = currentQuarterStart.subtract(3, 'quarters').startOf('quarter');

    const currentFinancialYearStart =
        now.month() < 3
            ? now
                  .year(now.year() - 1)
                  .startOf('year')
                  .add(3, 'month')
            : now.startOf('year').add(3, 'month');
    const previousFinancialYearStart = currentFinancialYearStart.subtract(1, 'year');
    const previousFinancialYearEnd = currentFinancialYearStart.subtract(1, 'day');
    // const yearlyStartDate = previousFinancialYearEnd.subtract(1, 'years').startOf('month');

    let toDate = '';
    let fromDate = '';
    switch (periodSelectorVariant) {
        case 'Monthly':
            toDate = previousMonthLastDate.format(DATE_FORMAT);
            fromDate = monthlyStartDate.format(DATE_FORMAT);
            break;
        case 'Quarterly':
            toDate = now.format(DATE_FORMAT);
            fromDate = quarterlyStartDate.format(DATE_FORMAT);
            break;
        case 'Yearly':
            toDate = now.format(DATE_FORMAT);
            fromDate = previousFinancialYearStart.format(DATE_FORMAT);
            break;
    }

    return [fromDate, toDate, previousMonthLastDate.format(DATE_FORMAT)];
}

export const getFinancialHeadersForDebtSchedule = (companyFinancialsDataUpdateDate, particularsLabel = 'Particulars (Cr)') => {
    const provisionalDataHeader = `Provisional${companyFinancialsDataUpdateDate ? ` (${companyFinancialsDataUpdateDate})*` : '*'}`;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    let startYear;
    startYear = currentYear - 3;

    // from january to march, we need to go one more year back
    if (currentMonth <= 3) {
        startYear = currentYear - 4;
    }

    return [
        {
            name: particularsLabel,
            key: 'classificationName',
        },
        {
            name: `FY${String(startYear + 1).slice(-2)}A`,
            key: String(startYear),
        },
        {
            name: `FY${String(startYear + 2).slice(-2)}A`,
            key: String(startYear + 1),
        },
        {
            name: `FY${String(startYear + 3).slice(-2)}A`,
            key: String(startYear + 2),
        },
        {
            name: provisionalDataHeader,
            key: 'provisional',
        },
    ];
};

export const getFinancialYear = (year: number, withPrefix = false, returnEndYear = false) => {
    const suffix = getFiscalYearSuffix(year, returnEndYear);
    return withPrefix ? `FY’${suffix}` : suffix;
};

export const getFiscalYearSuffix = (year, returnEndYear = false) => {
    if (!year || isNaN(year)) return null;

    const startYear = new Date(year, 0).getFullYear();
    const endYear = startYear + 1;
    if (returnEndYear) return endYear;
    return `${startYear.toString().slice(-2)}-${endYear.toString().slice(-2)}`;
};

export const getStartDateEndDate = () => {
    const now = dayjs();
    const currentMonth = now.month();
    const currentMonthBetweenAprilAndSeptember = currentMonth >= 3 && currentMonth <= 8;
    let startDate: string, endDate: string;

    if (currentMonthBetweenAprilAndSeptember) {
        endDate = now
            .month(2)
            .date(31)
            .year(now.year() - 1)
            .format('YYYY-MM-DD');
        startDate = now
            .month(3)
            .date(1)
            .year(now.year() - 7)
            .format('YYYY-MM-DD');
    } else {
        endDate = now.month(2).date(31).year(now.year()).format('YYYY-MM-DD');
        startDate = now
            .month(3)
            .date(1)
            .year(now.year() - 6)
            .format('YYYY-MM-DD');
    }
    return { startDate, endDate };
};

export const formatFinancialDate = (date: string) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    };
    return `As of ${new Intl.DateTimeFormat('en-GB', options).format(dateObj)}`;
};

export const checkIfDateIsValid = (date: Date): boolean => {
    if (!date || !(date instanceof Date)) return false;
    return dayjs(date).isValid();
};

export const formatDateString = (date: Date, displayFormat: string) => {
    if (!checkIfDateIsValid(date)) {
        return '';
    }

    if (!displayFormat || displayFormat.trim() === '') {
        return dayjs(date).format(DATE_FORMATS.ISO);
    }
    return dayjs(date).format(displayFormat);
};

export const getAuditedYear = (year: number) => {
    if (!year || isNaN(year)) return '';
    const cutoff = dayjs(`${year + 1}-03-31`);
    return dayjs().isAfter(cutoff) ? `${year + 1}` : `${year}`;
};

/**
 * Sorts an array of objects by a date field in descending order (latest first)
 * @param entries - Array of objects to sort
 * @param dateField - Name of the date field to sort by (defaults to 'lastUpdatedAt')
 * @returns Sorted array (latest first)
 */
export const sortByDateDescending = <T extends Record<string, any>>(entries: T[], dateField: string = 'lastUpdatedAt'): T[] => {
    return [...entries].sort((a, b) => {
        const dateA = a[dateField] ?? 0;
        const dateB = b[dateField] ?? 0;
        return dateB - dateA;
    });
};

/**
 * Returns the entry with the most recent value of `dateField` (an ISO date
 * string, nullable) — entries with no date never win against one that has a
 * date, but a non-empty list always returns something (mirrors Array.find's
 * "first match" fallback when no entry has a date at all). Unlike
 * sortByDateDescending (a native numeric `-`, which silently no-ops on ISO
 * date strings since they coerce to NaN), this parses each value through
 * dayjs for a correct chronological comparison.
 */
export const getMostRecentByDate = <T extends Record<string, any>>(entries: T[], dateField: string): T | undefined => {
    if (entries.length === 0) return undefined;
    return entries.reduce((latest, entry) => {
        const entryDate = entry[dateField];
        const latestDate = latest[dateField];
        if (!latestDate) return entryDate ? entry : latest;
        if (!entryDate) return latest;
        return dayjs(entryDate).isAfter(dayjs(latestDate)) ? entry : latest;
    }, entries[0]);
};

/**
 * Formats a timestamp (number) or date string using the specified format
 * @param timestamp - Timestamp in milliseconds (number) or date string
 * @param format - Date format string (e.g., 'DD MMM YYYY'). Defaults to ISO format if not provided
 * @returns Formatted date string or empty string if invalid
 */
export const formatDateByFormat = (timestamp: number | string | undefined, format?: string): string => {
    if (!timestamp) return '';
    const normalized = typeof timestamp === 'string' && /^\d+$/.test(timestamp) ? Number(timestamp) : timestamp;
    const date = dayjs(normalized);
    if (!date.isValid()) return '';
    return date.format(format || DATE_FORMATS.ISO);
};

/**
 * Same as `formatDateByFormat`, but converts to IST (Asia/Kolkata) before formatting, regardless
 * of the viewer's machine timezone. Use this — instead of `formatDateByFormat` — for any UI that
 * must agree with a backend artifact that pins IST (e.g. the Consent Audit Trail PDF, which
 * hardcodes `Asia/Kolkata` and appends " IST" to its timestamps). Pass a format from
 * `DATE_FORMATS` with a literal `[IST]` token so the zone is always labelled.
 */
export const formatDateInIST = (timestamp: number | string | undefined, format?: string): string => {
    if (!timestamp) return '';
    const normalized = typeof timestamp === 'string' && /^\d+$/.test(timestamp) ? Number(timestamp) : timestamp;
    const date = dayjs(normalized).tz(INDIA_TZ);
    if (!date.isValid()) return '';
    return date.format(format || DATE_FORMATS.ISO);
};

/** Format an "MM-YYYY" key (e.g. "03-2025") as "Mar 2025". */
export const formatMonthYearHeader = (monthHeader: string): string => {
    if (!monthHeader?.includes('-')) return monthHeader || '';
    const [mm, yyyy] = monthHeader.split('-');
    return dayjs(`${yyyy}-${mm}-01`, DATE_FORMATS.ISO).format('MMM YYYY');
};

/** Whole years between `begin` and `end` (defaults to now). Returns `null` if `begin` is missing/invalid. */
export const getYearsBetween = (begin?: number | string, end?: number | string): number | null => {
    if (!begin) return null;
    const startDate = dayjs(begin);
    if (!startDate.isValid()) return null;
    return (end ? dayjs(end) : dayjs()).diff(startDate, 'year');
};

/** Tenure-cell display string: "-", "< 1 year", or "N+ year(s)". */
export const formatTenureYears = (begin?: number, end?: number): string => {
    const years = getYearsBetween(begin, end);
    if (years == null) return '-';
    if (years <= 0) return '< 1 year';
    return `${years}+ year${years > 1 ? 's' : ''}`;
};

type DurationUnit = { threshold: number; unit: 'day' | 'hour' | 'minute' | 'second' };

const DURATION_UNITS: DurationUnit[] = [
    { threshold: 1, unit: 'day' },
    { threshold: 1 / 24, unit: 'hour' },
    { threshold: 1 / 1440, unit: 'minute' },
    { threshold: 1 / 86400, unit: 'second' },
];

/**
 * Returns a human-readable elapsed-time string between two timestamps.
 * Falls back to "just now" when the difference is less than one second.
 * @param fromEpoch  Start timestamp (epoch milliseconds, or any dayjs-parseable string).
 * @param toEpoch    End timestamp (epoch milliseconds, or any dayjs-parseable string). Defaults to now.
 */
export const calculateTimeSpent = (fromEpoch: number | string, toEpoch?: number | string): string => {
    const diff = dayjs.duration(dayjs(toEpoch).diff(dayjs(fromEpoch)));
    const asDays = diff.asDays();

    for (const { threshold, unit } of DURATION_UNITS) {
        const value = Math.floor(diff.as(unit));
        if (asDays >= threshold) {
            return `${value} ${unit}${value === 1 ? '' : 's'}`;
        }
    }

    return 'just now';
};

export type PartialDateGranularity = 'day' | 'month' | 'year';

/**
 * Parses management-profile style date strings that may carry literal
 * placeholder segments for an unknown day/month — e.g. "15-06-2020",
 * "DD-06-2020", "DD-MM-2020". A fully-placeholder value ("DD-MM-YYYY") or a
 * missing/placeholder year returns null — without a year there is nothing
 * safe to compute from. Missing day/month default to the 1st/January so the
 * result is always a real `dayjs` date, alongside the granularity that was
 * actually known so callers can pick an appropriate display format.
 */
export const parsePartialDate = (dateString?: string | null): { date: dayjs.Dayjs; granularity: PartialDateGranularity } | null => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const [dayPart, monthPart, yearPart] = parts;
    if (!yearPart || yearPart === 'YYYY') return null;

    const dayKnown = !!dayPart && dayPart !== 'DD';
    const monthKnown = !!monthPart && monthPart !== 'MM';

    const day = dayKnown ? Number(dayPart) : 1;
    const month = monthKnown ? Number(monthPart) : 1;
    const year = Number(yearPart);
    if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;

    // Zero-padded so this is genuine ISO-8601 and dayjs's native parser handles it. The
    // second `format` argument only works with the customParseFormat plugin, which this
    // module does not extend — passing 'YYYY-M-D' looked load-bearing but was ignored, and
    // an unpadded "2019-3-14" relies on non-standard Date parsing.
    const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = dayjs(iso);
    if (!date.isValid()) return null;

    const granularity: PartialDateGranularity = dayKnown ? 'day' : monthKnown ? 'month' : 'year';
    return { date, granularity };
};

/**
 * Displays a partial date at the precision that's actually known: "MMM YYYY"
 * when the day and/or month are known, "YYYY" when only the year is known.
 * Returns null when the string is unparseable (fully placeholder, or a
 * missing/placeholder year).
 */
export const formatPartialDateMonthYear = (dateString?: string | null): string | null => {
    const parsed = parsePartialDate(dateString);
    if (!parsed) return null;
    return parsed.granularity === 'year' ? parsed.date.format('YYYY') : parsed.date.format('MMM YYYY');
};

const toMonthIndex = (date: dayjs.Dayjs): number => date.year() * 12 + date.month();

/**
 * Whole months between two partial-date strings. `endDateString` missing or
 * unparseable is treated as "ongoing" and diffed against now. Returns null
 * when `startDateString` can't be parsed — never guesses at a duration from
 * an unknown start — or when the resolved range is negative.
 */
export const getPartialDateDurationMonths = (startDateString?: string | null, endDateString?: string | null): number | null => {
    const start = parsePartialDate(startDateString);
    if (!start) return null;
    const end = parsePartialDate(endDateString);
    const endDate = end ? end.date : dayjs();
    const months = toMonthIndex(endDate) - toMonthIndex(start.date);
    return months < 0 ? null : months;
};

/**
 * Directorship dates arrive as ISO (`2019-03-14`), while work-experience and education
 * dates arrive as partials (`DD-MM-YYYY`, often with literal placeholders). ISO has to be
 * detected FIRST: parsePartialDate reads the leading segment as the DAY, so it would
 * quietly resolve `2019-03-14` to year 14 rather than failing.
 */
export const parseFlexibleDate = (dateString?: string | null): dayjs.Dayjs | null => {
    if (!dateString) return null;
    const [firstSegment] = dateString.split('-');
    if (/^\d{4}$/.test(firstSegment)) {
        const isoDate = dayjs(dateString);
        return isoDate.isValid() ? isoDate : null;
    }
    return parsePartialDate(dateString)?.date ?? null;
};

/**
 * formatPartialDateMonthYear for a source whose date format isn't known up front — the
 * LinkedIn-enriched work-experience and education entries send ISO, while the MCA-derived
 * ones send DD-MM-YYYY partials.
 */
export const formatFlexibleDateMonthYear = (dateString?: string | null): string | null => {
    if (!dateString) return null;
    const [firstSegment] = dateString.split('-');
    if (/^\d{4}$/.test(firstSegment)) {
        const isoDate = dayjs(dateString);
        return isoDate.isValid() ? isoDate.format(DATE_FORMATS.MONTH_SHORT_YEAR) : null;
    }
    return formatPartialDateMonthYear(dateString);
};

/**
 * getPartialDateDurationMonths for a source whose date format isn't known up front.
 * Same rules: a missing or unparseable end means "ongoing" and is diffed against now,
 * and an unparseable start returns null rather than guessing at a duration.
 */
export const getFlexibleDurationMonths = (startDateString?: string | null, endDateString?: string | null): number | null => {
    const start = parseFlexibleDate(startDateString);
    if (!start) return null;
    const end = parseFlexibleDate(endDateString) ?? dayjs();
    const months = toMonthIndex(end) - toMonthIndex(start);
    return months < 0 ? null : months;
};

/**
 * "34" or "34 years" -> "34". The API is inconsistent about whether the unit is already
 * attached, so callers cannot use the raw value. Returns undefined for an absent or
 * unparseable age so the element can be dropped rather than rendered empty.
 */
export const formatAgeValue = (age?: string): string | undefined => {
    if (!age) return undefined;
    return String(age).match(/\d+/)?.[0];
};

/**
 * "34" or "34 years" -> "34 years". For a label standing on its own, where a bare number
 * reads as a typo. Where the number already sits beside a date, use {@link formatAgeValue}.
 */
export const formatAgeLabel = (age?: string): string | undefined => {
    const years = formatAgeValue(age);
    return years ? `${years} years` : undefined;
};

/**
 * A director's DOB for display, from a source whose format isn't known up front.
 *
 * The hover card is fed from two places that disagree: polus forwards the stored value
 * verbatim, so it arrives as ISO (`1974-12-02T00:00:00.000Z`), while the Management Profile
 * tab's own rows arrive already as `DD-MM-YYYY`. The card printed whichever it was given,
 * which is how a raw ISO timestamp reached the UI on three of the four surfaces.
 *
 * Returns null for an absent or unparseable value so the caller can drop the element rather
 * than render `Invalid Date`.
 */
export const formatDobDisplay = (dob?: string | null): string | null => {
    const parsed = parseFlexibleDate(dob);
    return parsed?.isValid() ? parsed.format(DATE_FORMATS.DATE_FORMAT_DDMMYYYY) : null;
};

/**
 * Age in whole years from a DOB, for sources that carry the date but not the age.
 *
 * `age` is not stored on a management profile — it exists on zero of them — so every
 * polus-fed surface has a DOB and no age. Derived here rather than in the backend so both
 * feeds land on the same number.
 *
 * Bounded to a plausible human range: a future or absurd DOB returns undefined instead of a
 * negative or four-digit age.
 */
export const deriveAgeFromDob = (dob?: string | null): string | undefined => {
    const parsed = parseFlexibleDate(dob);
    if (!parsed?.isValid()) return undefined;
    const years = dayjs().diff(parsed, 'year');
    return years >= 0 && years < 150 ? String(years) : undefined;
};

/**
 * "5y 2m" / "2y" / "8m" — precise elapsed-duration label for a single row.
 * Returns null for a sub-one-month (or negative/missing) duration rather
 * than printing "0m" or "NaN".
 */
export const formatPreciseDurationLabel = (totalMonths?: number | null): string | null => {
    if (totalMonths == null || totalMonths < 1) return null;
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    if (years === 0) return `${months}m`;
    return months === 0 ? `${years}y` : `${years}y ${months}m`;
};

/**
 * "10 years+" — rounded-down stat-card/summary duration label. Mirrors
 * `formatTenureYears`' "< 1 year" wording for sub-year durations.
 */
export const formatDurationYearsPlus = (totalMonths?: number | null): string | null => {
    if (totalMonths == null) return null;
    if (totalMonths < 12) return '< 1 year';
    return `${Math.floor(totalMonths / 12)} years+`;
};

/**
 * Total months covered by a set of [start, end] ranges, counting
 * overlapping/adjacent ranges only once — a person with two concurrent
 * roles hasn't worked twice as long. Ranges with an unparseable start are
 * ignored; a missing/unparseable end is treated as ongoing (diffed to now).
 *
 * Parses flexibly: the LinkedIn-enriched work history sends ISO dates, and reading those
 * with the partial-only parser silently produced a nonsense total (a 10-year career
 * collapsed to "< 1 year") because it took the 4-digit year as the day-of-month.
 */
export const getUnionedDurationMonths = (ranges: Array<{ start?: string | null; end?: string | null }>): number | null => {
    const intervals = ranges
        .map(({ start, end }) => {
            const parsedStart = parseFlexibleDate(start);
            if (!parsedStart) return null;
            const endDate = parseFlexibleDate(end) ?? dayjs();
            const startIdx = toMonthIndex(parsedStart);
            const endIdx = toMonthIndex(endDate);
            return endIdx >= startIdx ? ([startIdx, endIdx] as [number, number]) : null;
        })
        .filter((interval): interval is [number, number] => interval !== null)
        .sort((a, b) => a[0] - b[0]);

    // Nothing parseable is NOT a duration of zero. Returning 0 here made a list of entries with
    // unreadable start dates render as "< 1 year" on the stat card — a figure nothing computed.
    // A genuinely short (same-month) role still parses and still returns 0, which IS "< 1 year".
    if (!intervals.length) return null;

    let total = 0;
    let currentStart: number | null = null;
    let currentEnd = 0;

    for (const [start, end] of intervals) {
        if (currentStart === null) {
            currentStart = start;
            currentEnd = end;
            continue;
        }
        if (start <= currentEnd) {
            currentEnd = Math.max(currentEnd, end);
        } else {
            total += currentEnd - currentStart;
            currentStart = start;
            currentEnd = end;
        }
    }
    if (currentStart !== null) total += currentEnd - currentStart;
    return total;
};

/*
   End year of the current Indian financial year (Apr–Mar), evaluated in IST so
   the boundary flips at midnight India time regardless of the viewer's timezone
   (e.g. 8 PM EDT on 31 Mar is already 1 Apr in India). 16 Jul 2026 → FY26-27 → 2027;
   Jan–Mar belongs to the FY that began the previous calendar year. 
*/
export const getCurrentFinancialYearEndYear = (now: dayjs.ConfigType = dayjs()): number => {
    const ist = dayjs(now).tz(INDIA_TZ);
    return ist.month() >= 3 ? ist.year() + 1 : ist.year();
};

/**
 * Last COMPLETED Indian financial year (Apr-Mar), evaluated in IST via
 * getCurrentFinancialYearEndYear. E.g. any date inside FY26-27 (Apr 2026 -
 * Mar 2027, whose "end year" is 2027) returns the prior completed FY25-26:
 * { startDate: '2025-04-01', endDate: '2026-03-31' }.
 */
export const getLastCompletedFinancialYearRange = (now: dayjs.ConfigType = dayjs()): { startDate: string; endDate: string } => {
    const lastCompletedFYEndYear = getCurrentFinancialYearEndYear(now) - 1;
    return {
        startDate: dayjs(`${lastCompletedFYEndYear - 1}-04-01`).format(DATE_FORMATS.ISO),
        endDate: dayjs(`${lastCompletedFYEndYear}-03-31`).format(DATE_FORMATS.ISO),
    };
};

/**
 * Trailing 12 COMPLETED months — ends last month, not the current in-progress
 * one. Same rolling window `initializeDatepickerDates` already computes for
 * the 'Monthly' tenure; kept in local (non-IST) time to match it exactly.
 */
export const getTrailing12MonthsRange = (now: dayjs.ConfigType = dayjs()): { startDate: string; endDate: string } => {
    const endDate = dayjs(now).subtract(1, 'month').endOf('month');
    const startDate = endDate.subtract(11, 'months').startOf('month');
    return { startDate: startDate.format(DATE_FORMATS.ISO), endDate: endDate.format(DATE_FORMATS.ISO) };
};

/** First calendar day of a "YYYY-MM" month key, e.g. "2026-03" -> "2026-03-01". */
export const monthKeyToStartDate = (monthKey: string): string => dayjs(`${monthKey}-01`, DATE_FORMATS.ISO).format(DATE_FORMATS.ISO);

/** Last calendar day of a "YYYY-MM" month key, e.g. "2026-03" -> "2026-03-31". */
export const monthKeyToEndDate = (monthKey: string): string =>
    dayjs(`${monthKey}-01`, DATE_FORMATS.ISO).endOf('month').format(DATE_FORMATS.ISO);

/** Extracts the "YYYY-MM" month key from an ISO date string, e.g. "2026-03-15" -> "2026-03". */
export const dateToMonthKey = (date: string): string => (date ? date.slice(0, 7) : '');

/** Last COMPLETED month as a "YYYY-MM" key — the ceiling for month pickers that can't select the in-progress month. */
export const getLastCompletedMonthKey = (now: dayjs.ConfigType = dayjs()): string => dayjs(now).subtract(1, 'month').format('YYYY-MM');

/**
 * Buckets a timestamp into "Today" / "Yesterday" / "Older" — used to group a
 * date-descending list (e.g. conversation history) under section headers.
 * Falls back to "Older" for a null/invalid date rather than throwing, since a
 * missing timestamp shouldn't crash a list render.
 */
export const getRelativeDayGroup = (date?: string | null): 'Today' | 'Yesterday' | 'Older' => {
    if (!date) return 'Older';
    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) return 'Older';

    const diff = dayjs().startOf('day').diff(parsedDate.startOf('day'), 'day');
    if (diff <= 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return 'Older';
};

/**
 * Format a "YYYY-MM" month key as "Jan 2025".
 * Same output as the sibling `formatMonthYearHeader` above, which takes the OPPOSITE
 * key order ("MM-YYYY") — don't mix the two up.
 */
export const monthKeyToDisplay = (monthKey: string): string =>
    monthKey ? dayjs(`${monthKey}-01`, DATE_FORMATS.ISO).format(DATE_FORMATS.MONTH_SHORT_YEAR) : '';

/** Subtracts `n` months from a "YYYY-MM" key, e.g. ("2026-03", 11) -> "2025-04". */
export const subtractMonthsFromMonthKey = (monthKey: string, n: number): string =>
    dayjs(`${monthKey}-01`, DATE_FORMATS.ISO).subtract(n, 'month').format('YYYY-MM');

/**
 * Parses a "period header" — the column header underwriting-engine puts on each bank
 * statement strip, e.g. "Aug_2025" (format `MMM_YYYY`, produced by
 * `getAllDatesBetweenTwoDates`) — into a canonical "YYYY-MM" month key, e.g.
 * `periodHeaderToMonthKey('Aug_2025') === '2025-08'`. Hand-rolled rather than
 * `dayjs(value, 'MMM_YYYY')` — this module does not extend the customParseFormat plugin
 * (see `parsePartialDate`'s note above), so a custom format string is silently ignored and
 * falls through to native Date parsing, which cannot read "Aug_2025" at all (it isn't a
 * shape `new Date()`/dayjs's default parser recognizes). Returns '' when the value doesn't
 * match the expected `MMM_YYYY` shape.
 */
export const periodHeaderToMonthKey = (headerValue: string): string => {
    const match = /^([A-Za-z]{3})_(\d{4})$/.exec(headerValue || '');
    if (!match) return '';
    const monthKey = Object.keys(monthsEnum).find((key) => monthsEnum[key].name.toLowerCase() === match[1].toLowerCase());
    if (!monthKey) return '';
    return `${match[2]}-${monthKey}`;
};

/**
 * Maps an applied GST "Time Period" range onto the request params the GST backends accept.
 *
 * - LAST_FY / LAST_12M send only `rangeKey`, so the server resolves the canonical window
 *   (this keeps its cache keys stable instead of churning on FE-computed dates).
 * - CUSTOM sends the explicit month-snapped dates instead.
 * - ALL sends `rangeKey: 'ALL'` — no window, but a cacheable one.
 *
 * Lives here beside the range helpers that produce those dates, and deliberately NOT in
 * revenueVendorExpensesApiHelpers: suites that mock the API layer would mock this pure mapper
 * away with it.
 *
 * Shared by the GSTR-3B Summary table and the "Excel Analysis" report generate call so the
 * workbook can never be scoped differently from the screen it was generated from.
 */
export const buildGstRangeParams = (rangeKey: GstRangeKey, fetchStartDate: string, fetchEndDate: string): GstRangeParams => {
    if (rangeKey === GST_RANGE_KEYS.LAST_FY || rangeKey === GST_RANGE_KEYS.LAST_12M) return { rangeKey };
    if (rangeKey === GST_RANGE_KEYS.CUSTOM) return { startDate: fetchStartDate, endDate: fetchEndDate };
    return { rangeKey: GST_RANGE_KEYS.ALL };
};
